import React from "react";
import { Users, Heart, Handshake, ExternalLink, Building2, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import HeroSection from "@/components/HeroSection";
import Section from "@/components/Section";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/Button";

// ============================================
// IMMAGINI
// ============================================

// Hero image
const HERO_IMAGE = "/images/99-castelli-header.webp";

// Immagini dei partner (da Google Sites originale)
const EAGLE_PROJECT_LOGO = "/images/Eagle-Project.webp";
const CSI_LAQUILA_LOGO = "/images/CSI-LAquila.webp";

// ============================================
// ANIMAZIONI
// ============================================

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

// ============================================
// DATI PARTNER
// ============================================

interface Partner {
  name: string;
  logo: string;
  url: string;
  description: React.ReactNode;
  icon: React.ElementType;
}

const partners: Partner[] = [
  {
    name: "Eagle Project",
    logo: EAGLE_PROJECT_LOGO,
    url: "https://www.eagleproject.it",
    icon: Building2,
    description: (
      <>
        <p className="mb-4">
          Eagle Project è l'azienda informatica e di servizi alle imprese dove lavoro come dipendente da 20 anni: qui
          sono cresciuto e mi sento a casa!
        </p>
        <p className="mb-4">Siamo il partner informatico di diverse imprese, occupandoci per loro:</p>
        <ul className="list-disc list-inside space-y-2 mb-4 text-text-light">
          <li>
            di proteggere i dati dalla cancellazione accidentale, dalla rottura hardware e dagli attacchi esterni;
          </li>
          <li>della loro visibilità su web, creando il sito e gestendo le newsletter;</li>
          <li>di gestire clienti e le relazioni con software specializzati (CRM)...</li>
        </ul>
        <p>
          ...e molto altro ancora, mettendo sempre il cliente e le sue esigenze al centro di tutto! Grazie a Eagle
          Project ho potuto realizzare questo sito!
        </p>
      </>
    ),
  },
  {
    name: "CSI L'Aquila",
    logo: CSI_LAQUILA_LOGO,
    url: "https://comitatocsilaquila.it/",
    icon: Trophy,
    description: (
      <p>
        Abbiamo deciso di intraprendere questa avventura affiliandoci al CSI perché ne condividiamo il pensiero e le
        finalità, ma soprattutto perché nel Comitato di L'Aquila abbiamo incontrato una squadra giovane e con tanta
        voglia di crescere, con cui ci siamo subito trovati bene... sicuramente percorreremo molta strada insieme!
      </p>
    ),
  },
];

// ============================================
// COMPONENTE PARTNER CARD
// ============================================

interface PartnerCardProps {
  partner: Partner;
  index: number;
}

const PartnerCard: React.FC<PartnerCardProps> = ({ partner, index }) => {
  const isEven = index % 2 === 0;
  const Icon = partner.icon;

  return (
    <motion.div
      variants={isEven ? fadeInLeft : fadeInRight}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      className={`flex flex-col ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"} gap-8 items-center`}
    >
      {/* Logo */}
      <div className="lg:w-2/5">
        <a href={partner.url} target="_blank" rel="noopener noreferrer" className="block group">
          <div className="bg-white rounded-2xl shadow-card border border-border overflow-hidden hover:shadow-lg transition-all duration-300 group-hover:scale-[1.02]">
            <div className="aspect-video p-6 flex items-center justify-center bg-gray-50">
              <img src={partner.logo} alt={`Logo ${partner.name}`} className="max-w-full max-h-full object-contain" />
            </div>
            <div className="p-4 border-t border-border flex items-center justify-between bg-white">
              <div className="flex items-center gap-2 text-primary">
                <Icon size={20} />
                <span className="font-semibold">{partner.name}</span>
              </div>
              <ExternalLink size={18} className="text-text-light group-hover:text-primary transition-colors" />
            </div>
          </div>
        </a>
      </div>

      {/* Description */}
      <div className="lg:w-3/5">
        <div className="bg-surface rounded-2xl p-6 md:p-8 shadow-card border border-border">
          <h3 className="text-2xl font-display font-bold text-primary mb-4 flex items-center gap-3">
            <Icon size={28} className="text-primary" />
            {partner.name}
          </h3>
          <div className="text-text-light leading-relaxed">{partner.description}</div>
          <div className="mt-6">
            <Button
              variant="primary"
              size="md"
              onClick={() => window.open(partner.url, "_blank")}
              className="inline-flex items-center gap-2"
            >
              Visita il sito
              <ExternalLink size={16} />
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// COMPONENTE PRINCIPALE
// ============================================

const Amici: React.FC = () => {
  return (
    <PageLayout>
      {/* Hero Section */}
      <HeroSection
        title="Amici"
        subtitle="Le collaborazioni e i partner che ci aiutano a crescere"
        backgroundImage={HERO_IMAGE}
        height="medium"
      />

      {/* Intro Section */}
      <Section background="white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-text mb-6">
              I nostri <span className="text-primary">Amici</span>
            </h2>
            <p className="text-lg text-text-light leading-relaxed max-w-3xl mx-auto">
              Per noi di ASD 99 Castelli la relazione con altre Associazioni, con Enti, con Aziende e con privati è
              fondamentale per crescere.
              <span className="block mt-2 font-semibold text-primary">
                Per questo consideriamo chiunque ci aiuti un amico!
              </span>
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Partners Section */}
      <Section background="gray">
        <div className="max-w-6xl mx-auto">
          <SectionHeader title="I nostri Partner" subtitle="Le realtà che ci supportano nel nostro percorso" />

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-16"
          >
            {partners.map((partner, index) => (
              <PartnerCard key={partner.name} partner={partner} index={index} />
            ))}
          </motion.div>
        </div>
      </Section>

      {/* Values Section */}
      <Section background="white">
        <div className="max-w-5xl mx-auto">
          <SectionHeader
            title="Perché le partnership sono importanti"
            subtitle="I valori alla base delle nostre collaborazioni"
          />

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: Users,
                title: "Condivisione",
                description: "Condividiamo valori, obiettivi e la passione per il territorio con i nostri partner.",
              },
              {
                icon: Handshake,
                title: "Collaborazione",
                description: "Lavoriamo insieme per creare opportunità e raggiungere traguardi comuni.",
              },
              {
                icon: Heart,
                title: "Sostegno reciproco",
                description: "Ci supportiamo a vicenda nelle sfide e celebriamo insieme i successi.",
              },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                variants={fadeInUp}
                className="bg-surface rounded-2xl p-6 shadow-card border border-border text-center hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-display font-bold text-text mb-3">{value.title}</h3>
                <p className="text-text-light">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="primary" className="text-center">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <Handshake className="w-16 h-16 text-white/80 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Vuoi diventare nostro partner?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Se condividi i nostri valori e vuoi collaborare con noi, contattaci! Siamo sempre aperti a nuove partnership
            e collaborazioni.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              onClick={() => (window.location.href = "mailto:99castelli@gmail.com")}
            >
              Contattaci via Email
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => (window.location.href = "/associazione")}
              className="border-white text-white hover:bg-white hover:text-primary"
            >
              Scopri chi siamo
            </Button>
          </div>
        </motion.div>
      </Section>
    </PageLayout>
  );
};

export default Amici;
